import { applyTheme } from './utils/applyTheme.mjs';
import { logger } from './utils/logger.mjs';
import { THEME, LOCAL_STORAGE_KEY } from './utils/constants.mjs';
import { loadTheme } from './themes/index.mjs';

var _a;
class ThemeCore {
    constructor() {
        var _a, _b, _c, _d, _e;
        this.listeners = new Set();
        this.select = (theme) => {
            var _a, _b;
            if (theme === 'auto') {
                (_a = window.localStorage) === null || _a === void 0 ? void 0 : _a.removeItem(LOCAL_STORAGE_KEY);
            }
            else {
                (_b = window.localStorage) === null || _b === void 0 ? void 0 : _b.setItem(LOCAL_STORAGE_KEY, theme);
            }
            this.updateTheme();
        };
        this.updateTheme = () => {
            var _a;
            const previousScheme = (_a = this.activeTheme) === null || _a === void 0 ? void 0 : _a.name;
            const scheme = this.chooseScheme();
            const [activeTheme, childThemes] = loadTheme(scheme);
            this.activeTheme = activeTheme;
            this.activeChildThemes = childThemes;
            applyTheme(this.activeTheme, this.activeChildThemes);
            this.informListeners();
            if (previousScheme !== scheme) {
                logger.log(`Updated theme to "${scheme}"`);
            }
        };
        this.autoThemeName = () => {
            const dark = this.dark.matches;
            const highContrast = this.highContrast.matches;
            if (dark && highContrast)
                return 'high_contrast_dark';
            if (!dark && highContrast)
                return 'high_contrast_light';
            if (dark && !highContrast)
                return 'dark';
            return 'light';
        };
        this.onThemeChange = (cb, runNow = false) => {
            this.listeners.add(cb);
            if (runNow) {
                try {
                    cb(this.activeTheme, this.activeChildThemes);
                }
                catch (error) {
                    logger.error(error);
                }
            }
            return () => {
                this.listeners.delete(cb);
            };
        };
        this.chooseScheme = () => {
            if (this.selected !== 'auto')
                return this.selected;
            return this.autoThemeName();
        };
        this.informListeners = () => {
            for (const fn of this.listeners) {
                try {
                    fn(this.activeTheme, this.activeChildThemes);
                }
                catch (error) {
                    logger.error(error);
                }
            }
        };
        this.dark = matchMedia('(prefers-color-scheme: dark)');
        (_b = (_a = this.dark) === null || _a === void 0 ? void 0 : _a.addEventListener) === null || _b === void 0 ? void 0 : _b.call(_a, 'change', this.updateTheme);
        this.highContrast = matchMedia('(prefers-contrast: more)');
        (_d = (_c = this.highContrast) === null || _c === void 0 ? void 0 : _c.addEventListener) === null || _d === void 0 ? void 0 : _d.call(_c, 'change', this.updateTheme);
        if (window[THEME] == null) {
            window[THEME] = this;
        }
        // we're in an iframe, so listen to the parent theme changes
        if (((_e = window.parent) === null || _e === void 0 ? void 0 : _e[THEME]) && this !== window.parent[THEME]) {
            const unsubscribe = window.parent[THEME].onThemeChange(({ name }) => {
                this.select(name);
            });
            window.addEventListener('unload', unsubscribe);
        }
        this.updateTheme();
    }
    get selected() {
        var _a, _b;
        return (_b = (_a = window.localStorage) === null || _a === void 0 ? void 0 : _a.getItem(LOCAL_STORAGE_KEY)) !== null && _b !== void 0 ? _b : 'auto';
    }
}
/**
 * The global theme object, for getting info about and controlling the currently active theme.
 * @usage ./theme.usage.md
 */
const themeCore = (_a = window[THEME]) !== null && _a !== void 0 ? _a : new ThemeCore();

export { themeCore };
